import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { useBuild } from '../contexts/BuildContext';

const AiLabelsSection2 = ({ onUpdate, boostInput = false }) => {
  const { fetchCachedList, frequentValuesIndex } = useBuild();
  const [currentLabelPrefix, setCurrentLabelPrefix] = useState('');
  const [currentLabel, setCurrentLabel] = useState('');
  const [currentField, setCurrentField] = useState('primary_labels');
  const [boost, setBoost] = useState(1);
  const [labelOptions, setLabelOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Label prefixes and filtered labels
  const [labelPrefixes, setLabelPrefixes] = useState([]);
  const [filteredLabels, setFilteredLabels] = useState([]);

  const fieldOptions = [
    { id: 'primary_labels', label: 'Primary Labels' },
    { id: 'secondary_labels', label: 'Secondary Labels' }
  ];

  // Fetch label options on component mount and when selectedIndex changes
  useEffect(() => {
    const fetchLabelOptions = async () => {
      try {
        setLoading(true);
        setError(null);
        const endpoint = `/search/frequent_values/${frequentValuesIndex}/primary_labels`;
        const labelsData = await fetchCachedList(endpoint);
        setLabelOptions(labelsData);
      } catch (err) {
        console.error('Failed to fetch label options:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLabelOptions();
  }, [fetchCachedList, frequentValuesIndex]);

  // Extract prefixes from labels and update filtered labels when data changes
  useEffect(() => {
    if (labelOptions.length > 0) {
      const prefixes = [...new Set(labelOptions.map(label => {
        const colonIndex = label.id.indexOf(':');
        return colonIndex > 0 ? label.id.substring(0, colonIndex) : 'Other';
      }))].sort();
      
      setLabelPrefixes(prefixes);
      
      // Set default prefix if none selected
      if (!currentLabelPrefix && prefixes.length > 0) {
        setCurrentLabelPrefix(prefixes[0]);
      }
    }
  }, [labelOptions, currentLabelPrefix]);

  // Filter labels based on selected prefix
  useEffect(() => {
    if (currentLabelPrefix && labelOptions.length > 0) {
      const filtered = labelOptions.filter(label => {
        const colonIndex = label.id.indexOf(':');
        const prefix = colonIndex > 0 ? label.id.substring(0, colonIndex) : 'Other';
        return prefix === currentLabelPrefix;
      });
      setFilteredLabels(filtered);
      
      // Reset selected label when prefix changes
      setCurrentLabel('');
    }
  }, [currentLabelPrefix, labelOptions]);

  const handleLabelAction = (actionType) => {
    if (!currentLabel || !currentField) return;
    
    const updateData = {
      action: actionType,
      filter: 'terms',
      field: currentField,
      value: [currentLabel]
    };
    
    // Add boost value if boostInput is enabled
    if (boostInput) {
      updateData.boost = boost;
    }
    
    onUpdate(updateData);
    // Reset after adding
    setCurrentLabel('');
    setCurrentField('primary_labels');
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">
            Failed to load labels: {error}
          </Alert>
        </Box>
      )}

      {error ? (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Label"
          value={currentLabel}
          onChange={(e) => setCurrentLabel(e.target.value)}
          placeholder="Enter label manually"
          disabled={loading}
        />
      ) : (
        <>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Prefix</InputLabel>
            <Select
              value={currentLabelPrefix}
              label="Prefix"
              onChange={(e) => setCurrentLabelPrefix(e.target.value)}
              disabled={loading}
            >
              {labelPrefixes.map((prefix) => (
                <MenuItem key={prefix} value={prefix}>
                  {prefix}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Label</InputLabel>
            <Select
              value={currentLabel}
              label="Label"
              onChange={(e) => setCurrentLabel(e.target.value)}
              disabled={loading || !currentLabelPrefix}
            >
              {filteredLabels.map((label) => (
                <MenuItem key={label.id} value={label.id}>
                  {label.id} ({label.count})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}
        
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Field</InputLabel>
        <Select
          value={currentField}
          label="Field"
          onChange={(e) => setCurrentField(e.target.value)}
          disabled={loading}
        >
          {fieldOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
        
      {boostInput && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Boost"
          type="number"
          value={boost}
          onChange={(e) => setBoost(parseFloat(e.target.value) || 1)}
          inputProps={{ min: 0, step: 0.1 }}
          disabled={loading}
        />
      )}
        
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleLabelAction('include')}
          disabled={!currentLabel || !currentField || loading}
        >
          <AddIcon />
        </Button>
        
        {boostInput && (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleLabelAction('boost')}
            disabled={!currentLabel || !currentField || loading}
          >
            <TrendingUpIcon />
          </Button>
        )}
        
        <Button
          variant="contained"
          color="error"
          onClick={() => handleLabelAction('exclude')}
          disabled={!currentLabel || !currentField || loading}
        >
          <RemoveIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default AiLabelsSection2;
